import { TimeInDeliveryContainerStatsDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useImmer } from 'use-immer';
import { useQueryTimeInLocker } from '../dashboard.queries';

export interface MedianTimeInLockerState {
  completedEventType: 'collected' | 'retrieved' | 'completed';
  fromDate: string;
  toDate: string;
}

export function useMedianTimeInLocker(projectID: string) {
  const [state, setState] = useImmer<MedianTimeInLockerState>(setInitialState());

  const baseData = useQueryTimeInLocker(projectID, {
    fromDate: state.fromDate,
    toDate: state.toDate,
    includeChildProjects: true
  });

  const data = useMemo(() => {
    return {
      isLoading: baseData.isLoading,
      refetch: baseData.refetch,
      medianTime: deriveMedianTimeInLocker(baseData.data, state.completedEventType)
    };
  }, [baseData, state.completedEventType]);

  const handlers = {
    onChangeEventType(completedEventType: MedianTimeInLockerState['completedEventType']) {
      setState((draft) => {
        draft.completedEventType = completedEventType;
      });
    }
  };

  return { data, handlers, state } as const;
}

function setInitialState(): MedianTimeInLockerState {
  const startOfToday = dayjs().startOf('day').toISOString();
  const initialFromDate = dayjs(startOfToday).subtract(1, 'month').toISOString();

  return {
    completedEventType: 'collected',
    fromDate: initialFromDate,
    toDate: startOfToday
  };
}

function deriveMedianTimeInLocker(
  data: TimeInDeliveryContainerStatsDto | undefined,
  completedEventType: MedianTimeInLockerState['completedEventType']
) {
  switch (completedEventType) {
    case 'collected':
      return data?.collectedEventMedianTime;
    case 'retrieved':
      return data?.retrievedEventMedianTime;
    default:
      return data?.medianTime;
  }
}
